import React, { useState, useEffect } from "react";
import Card from "./ui/Card";
import { formatCurrency } from "../utils/helpers";
import { APP_DOMAIN } from "../utils/constants";
import { supabase } from "../lib/supabase";
import { referralService } from "../lib/referralService";
import {
  Copy,
  Users,
  TrendingUp,
  DollarSign,
  Clock,
  Calendar,
} from "lucide-react";

const ReferralCenter = ({ affiliateName }) => {
  // Always expanded on dedicated page
  const isExpanded = true;
  const [referralHistory, setReferralHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referralCode, setReferralCode] = useState("");

  const referralLink = `${window.location.origin}/invite/${referralCode}`;

  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0,
    thisMonthReferrals: 0,
    thisMonthEarnings: 0,
    activeReferrals: 0,
    pendingBonus: 0,
  });

  const [weeklyBonuses, setWeeklyBonuses] = useState([]);

  useEffect(() => {
    if (affiliateName) {
      loadReferralData();
    }
  }, [affiliateName]);

  const loadReferralData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`🚀 Loading optimized referral data for ${affiliateName}`);

      // Generate referral code from affiliate name
      const code = affiliateName.toLowerCase().replace(/[^a-z0-9]/g, "");
      setReferralCode(code);

      // Use the optimized getReferralStats function with caching and consistent calculations
      const stats =
        await referralService.getReferralStatsOptimized(affiliateName);

      if (stats.error) {
        throw new Error(stats.error);
      }

      console.log(`✅ Referral stats loaded:`, {
        totalReferrals: stats.totalReferrals,
        totalEarnings: stats.totalReferralEarnings,
        activeReferrals: stats.activeReferrals,
      });

      // Set referral history from the optimized stats (already includes earnings data)
      setReferralHistory(stats.referrals || []);

      // Load weekly bonus breakdown
      const weeklyData = await referralService.getWeeklyReferralBonuses(
        affiliateName,
        12
      );
      setWeeklyBonuses(weeklyData);

      // Calculate current month stats from the referrals data
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();

      const thisMonthReferrals = stats.referrals.filter((ref) => {
        const refDate = new Date(ref.invited_at);
        return (
          refDate.getMonth() === currentMonth &&
          refDate.getFullYear() === currentYear
        );
      }).length;

      // Use the optimized data structure
      const thisMonthEarnings = stats.currentMonthEarnings || 0;
      const pendingBonus = stats.pendingBonusAmount || 0;

      // Set the properly calculated stats using the optimized data
      setReferralStats({
        totalReferrals: stats.totalReferrals,
        totalEarnings: stats.totalReferralEarnings,
        thisMonthReferrals: thisMonthReferrals,
        thisMonthEarnings: thisMonthEarnings,
        activeReferrals: stats.activeReferrals,
        pendingBonus: pendingBonus,
      });

      console.log(`✅ Referral data loaded successfully for ${affiliateName}`);
    } catch (err) {
      console.error("❌ Error loading referral data:", err);
      setError(`Failed to load referral data: ${err.message}`);

      // Set fallback empty data
      setReferralStats({
        totalReferrals: 0,
        totalEarnings: 0,
        thisMonthReferrals: 0,
        thisMonthEarnings: 0,
        activeReferrals: 0,
        pendingBonus: 0,
      });
      setReferralHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const copyReferralLink = () => {
    const referralLink = `https://${APP_DOMAIN}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    alert("Referral link copied to clipboard!");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      alert("Referral link copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return "bg-green-500/20 text-green-300";
      case "pending":
        return "bg-yellow-500/20 text-yellow-300";
      case "declined":
        return "bg-red-500/20 text-red-300";
      default:
        return "bg-gray-500/20 text-gray-300";
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: "bg-green-500/20 border-green-500/30 text-green-400",
      pending: "bg-yellow-500/20 border-yellow-500/30 text-yellow-400",
      inactive: "bg-gray-500/20 border-gray-500/30 text-gray-400",
    };

    return `${colors[status] || colors.inactive} px-3 py-1 rounded-full text-xs font-medium border`;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Referrals */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">
                Total Referrals
              </div>
              <div className="text-2xl font-bold text-white">
                {referralStats.totalReferrals}
              </div>
            </div>
          </div>
        </div>

        {/* Total Earnings */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#B8460E]/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-[#B8460E]" />
            </div>
            <div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">
                Total Earnings
              </div>
              <div className="text-2xl font-bold text-[#B8460E]">
                {formatCurrency(referralStats.totalEarnings)}
              </div>
            </div>
          </div>
        </div>

        {/* This Month */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">
                This Month
              </div>
              <div className="text-2xl font-bold text-white">
                {referralStats.thisMonthReferrals}
              </div>
            </div>
          </div>
        </div>

        {/* Pending Bonus */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">
                Pending Bonus
              </div>
              <div className="text-2xl font-bold text-[#D2691E]">
                {formatCurrency(referralStats.pendingBonus)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Link Section */}
      <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Your Referral Link</h2>
        </div>
        <div className="bg-black/60 border border-white/10 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="flex-1 px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B8460E]/50"
            />
            <button
              onClick={copyToClipboard}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-[#B8460E] hover:bg-[#8B3A0E] text-white rounded-lg transition-colors sm:w-auto w-full"
            >
              <Copy size={16} />
              <span>Copy</span>
            </button>
          </div>
        </div>
      </div>

      {/* Referral History */}
      <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Referral History</h2>
          <span className="text-sm text-gray-400">
            {referralHistory.length} total referrals
          </span>
        </div>

        <div className="space-y-3">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
              <p className="text-gray-400">Loading referral history...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-400 mb-4">Error loading referral data</p>
              <button
                onClick={loadReferralData}
                className="px-4 py-2 bg-[#B8460E] hover:bg-[#8B3A0E] text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          ) : referralHistory.length > 0 ? (
            referralHistory.map((referral, index) => (
              <div
                key={`${referral.referee_name}_${index}`}
                className="bg-black/30 border border-white/10 rounded-lg p-4 flex items-center justify-between hover:bg-black/40 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                    <Users size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-white">
                      {referral.referee_name || "New Affiliate"}
                    </div>
                    <div className="text-sm text-gray-400">
                      {referral.type === "connection" ? "Signed up" : "Invited"}{" "}
                      {new Date(referral.invited_at).toLocaleDateString()}
                    </div>
                    {referral.type === "invitation" &&
                      referral.invitation_code && (
                        <div className="text-xs text-blue-400">
                          Code: {referral.invitation_code}
                        </div>
                      )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[#B8460E] font-medium">
                    {referral.formattedEarnings ||
                      formatCurrency(referral.earnings || 0)}
                  </div>
                  <div className="text-sm text-gray-400">earned</div>
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getStatusColor(
                      referral.status
                    )}`}
                  >
                    {referral.status}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No referrals yet</p>
              <p className="text-sm">
                Share your referral link to get started!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Weekly Referral Earnings Breakdown */}
      <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            Weekly Referral Earnings
          </h2>
          <span className="text-sm text-gray-400">
            Recent earnings breakdown
          </span>
        </div>

        <div className="space-y-3">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#B8460E] mx-auto mb-4"></div>
              <p className="text-gray-400">Loading weekly earnings...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-400 mb-4">Error loading weekly earnings</p>
              <button
                onClick={loadReferralData}
                className="px-4 py-2 bg-[#B8460E] hover:bg-[#8B3A0E] text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          ) : weeklyBonuses.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No weekly earnings yet</p>
              <p className="text-sm">
                Earnings will appear here when your referrals start generating
                revenue.
              </p>
            </div>
          ) : (
            weeklyBonuses.slice(0, 8).map((week, index) => (
              <div
                key={`week_${week.weekStart}_${index}`}
                className="bg-black/30 border border-white/10 rounded-lg p-4 hover:bg-black/40 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-white">
                    Week of{" "}
                    {new Date(week.weekStart).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}{" "}
                    -{" "}
                    {new Date(week.weekEnd).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-[#B8460E] font-bold">
                      {formatCurrency(week.totalBonus)}
                    </div>
                    {!week.isPaid && (
                      <span className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 rounded text-xs">
                        Pending
                      </span>
                    )}
                    {week.isPaid && (
                      <span className="px-2 py-1 bg-green-500/20 border border-green-500/30 text-green-400 rounded text-xs">
                        Paid
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-400">
                  From {week.referees.length} referral
                  {week.referees.length !== 1 ? "s" : ""} • Total referee
                  earnings: {formatCurrency(week.totalRefereeEarnings)}
                  <div className="mt-2 space-y-1">
                    {week.referees.slice(0, 4).map((ref, refIndex) => (
                      <div
                        key={refIndex}
                        className="flex justify-between items-center"
                      >
                        <span className="text-white text-sm">{ref.name}</span>
                        <div className="text-right">
                          <span className="text-[#B8460E] font-medium">
                            {formatCurrency(ref.bonus)}
                          </span>
                          <span className="text-gray-400 text-xs ml-2">
                            (from {formatCurrency(ref.earnings)})
                          </span>
                        </div>
                      </div>
                    ))}
                    {week.referees.length > 4 && (
                      <div className="text-center text-gray-500 text-xs">
                        +{week.referees.length - 4} more
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Referral Program Info */}
      <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-[#B8460E]/20 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-[#B8460E] font-bold text-lg">1</span>
            </div>
            <h3 className="font-semibold text-white mb-2">Share Your Link</h3>
            <p className="text-sm text-gray-400">
              Send your unique referral link to potential affiliates
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-[#B8460E]/20 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-[#B8460E] font-bold text-lg">2</span>
            </div>
            <h3 className="font-semibold text-white mb-2">They Sign Up</h3>
            <p className="text-sm text-gray-400">
              When someone registers using your link, you get credit
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-[#B8460E]/20 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-[#B8460E] font-bold text-lg">3</span>
            </div>
            <h3 className="font-semibold text-white mb-2">Earn Bonuses</h3>
            <p className="text-sm text-gray-400">
              Get paid when your referrals start generating revenue
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralCenter;
